import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ProfilePage from './pages/ProfilePage';
import Dashboard from './pages/Dashboard';
import ClientsPage from './pages/ClientsPage';
import ClientDetail from './pages/ClientDetail';
import IncomeTaxPage from './pages/IncomeTaxPage';
import TDSPage from './pages/TDSPage';
import GSTPage from './pages/GSTPage';
import AuditPage from './pages/AuditPage';
import ROCPage from './pages/ROCPage';
import ESIPFPage from './pages/ESIPFPage';
import TasksPage from './pages/TasksPage';
import MeetingsPage from './pages/MeetingsPage';
import MeetingDetailPage from './pages/MeetingDetailPage';
import ActionItemsPage from './pages/ActionItemsPage';
import ChecklistsPage from './pages/ChecklistsPage';
import ChecklistDetailPage from './pages/ChecklistDetailPage';
import BillingPage from './pages/BillingPage';
import DocumentsPage from './pages/DocumentsPage';
import ReportsPage from './pages/ReportsPage';
import CalendarPage from './pages/CalendarPage';
import GlobalShortcuts from './components/GlobalShortcuts';
import WorkLedgerPage from './pages/WorkLedgerPage';
import { ThemeProvider, useTheme } from './themes/ThemeContext';

// Lazy-loaded pages
const AccountsPage = lazy(() => import('./pages/AccountsPage'));
const ArchivedClientsPage = lazy(() => import('./pages/ArchivedClientsPage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const MyWorkPage = lazy(() => import('./pages/MyWorkPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DSCPage = lazy(() => import('./pages/DSCPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const PersonalSpacePage = lazy(() => import('./pages/PersonalSpacePage'));
// Tools pulls in pdf-lib / pdf.js — lazy so the main bundle is unaffected
const ToolsPage = lazy(() => import('./pages/ToolsPage'));

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-[#1e3a5f]"><div className="text-white text-lg">Loading...</div></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.force_password_change) return <Navigate to="/change-password" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!['partner', 'senior_staff', 'super_admin', 'admin'].includes(user.role)) return <Navigate to="/" />;
  return children;
}

// Accessible to any logged-in user; passes forced=true when flag is set
function AuthedChangePassword() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  return <ChangePasswordPage forced={!!user.force_password_change} />;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!['partner', 'super_admin'].includes(user.role)) return <Navigate to="/" />;
  return children;
}

const Fallback = () => <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

function AppRoutes() {
  return (
    <NotificationProvider>
      <GlobalShortcuts />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/change-password" element={<AuthedChangePassword />} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="clients/:clientId/work-ledger" element={<WorkLedgerPage />} />
          <Route path="income-tax" element={<IncomeTaxPage />} />
          <Route path="tds" element={<TDSPage />} />
          <Route path="gst" element={<GSTPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="roc" element={<ROCPage />} />
          <Route path="esipf" element={<ESIPFPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="meetings" element={<MeetingsPage />} />
          <Route path="meetings/action-items" element={<ActionItemsPage />} />
          <Route path="meetings/:id" element={<MeetingDetailPage />} />
          <Route path="checklists" element={<ChecklistsPage />} />
          <Route path="checklists/:id" element={<ChecklistDetailPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="dsc" element={<Suspense fallback={<Fallback />}><DSCPage /></Suspense>} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="notifications" element={<Suspense fallback={<Fallback />}><NotificationsPage /></Suspense>} />
          <Route path="my-work" element={<Suspense fallback={<Fallback />}><MyWorkPage /></Suspense>} />
          <Route path="personal" element={<Suspense fallback={<Fallback />}><PersonalSpacePage /></Suspense>} />
          <Route path="tools" element={<Suspense fallback={<Fallback />}><ToolsPage /></Suspense>} />
          <Route path="accounts/*" element={<Suspense fallback={<Fallback />}><AccountsPage /></Suspense>} />
          <Route path="archived-clients" element={<AdminRoute><Suspense fallback={<Fallback />}><ArchivedClientsPage /></Suspense></AdminRoute>} />
          <Route path="employees" element={<AdminRoute><Suspense fallback={<Fallback />}><EmployeesPage /></Suspense></AdminRoute>} />
          <Route path="settings" element={<SuperAdminRoute><Suspense fallback={<Fallback />}><SettingsPage /></Suspense></SuperAdminRoute>} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>
      </Routes>
    </NotificationProvider>
  );
}

// iOS-style bottom-center toasts in Cupertino; default top-right elsewhere
function ThemedToaster() {
  const { themeId } = useTheme();
  if (themeId === 'cupertino') {
    return (
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1D1D1F',
            color: '#ffffff',
            fontSize: '13px',
            borderRadius: '999px',
            padding: '10px 20px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
          },
        }}
      />
    );
  }
  return <Toaster position="top-right" toastOptions={{ duration: 3000 }} />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <ThemedToaster />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
